import React, { useRef } from "react";
import { Container, Row, Col, Tab, Nav, Spinner, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useContextValues } from "../context/RouteDataContext.jsx";
import RouteAttributeControls from "../components/RouteAttributeControls.jsx";
import ScheduleContainer from "../components/schedule-components/ScheduleContainer.jsx";
import RealtimeContainer from "../components/realtime-components/RealtimeContainer.jsx";
import RiderAlerts from "../components/RiderAlerts.jsx";
import Alert from "../components/Alert.jsx";
import { APP_STATES } from "../utils/constants.js";
import { trackEvent } from "../utils/analytics.js";

/**
 * App - Main application component using centralized RouteDataContext
 */
export default function RoutePage() {
  const { routeId, routeShortName, directionId, selectedDate, timeUnit, stopsUnit, extendedFields, appState, currentView, setCurrentView } = useContextValues();
  const routePageRef = useRef(null);

  const handleDownloadClick = () => {
    trackEvent("route_schedule_download", { route_id: routeId });
  };

  const downloadPath = routeShortName
    ? `/plan-your-trip/routes/${routeShortName}/download`
    : null;

  let statusMessage = "";
  let statusContent = "";
  let completionMessage = "";

  if (appState === APP_STATES.READY) {
    completionMessage = `Route ${routeShortName} has finished loading.`;
  }

  if (appState === APP_STATES.LOADING) {
    statusMessage = "Fetching route information...";
    statusContent = (
      <>
        <Spinner animation="border" size="sm" className="me-1" aria-hidden="true" />
        {statusMessage}
      </>
    );
  }

  if (appState === APP_STATES.EMPTY) {
    statusMessage = "This route does not operate on the requested date.";
    statusContent = statusMessage;
  }

  if (appState === APP_STATES.ERROR) {
    statusMessage = "Could not load the requested route. Please consult Plan Your Trip for route planning help or refresh this page.";
    statusContent = (
      <>
        Could not load the requested route. Please consult <a href="/plan-your-trip">Plan Your Trip</a> for route planning help or refresh this page.
      </>
    );
  }

  return (
    // These data attributes provide stable controls for synthetic monitoring.
    // @see RouteAttributeControls for handling of the attributes.
    <div
      ref={routePageRef}
      className="ictapp__route_page"
      data-route={routeId}
      data-direction={directionId}
      data-date={selectedDate}
      data-view={currentView}
      data-time-unit={timeUnit}
      data-show-only={stopsUnit}
      data-show-extended-fields={extendedFields ? "true" : "false"}
      aria-busy={appState === APP_STATES.LOADING}
    >
      <RouteAttributeControls targetRef={routePageRef} />
      <Alert hidden>{completionMessage}</Alert>
      <Row>
        <Col xs={12}>
          {/* DOWNLOAD SCHEDULE */}
          <Button
            {...(downloadPath
              ? {
                as: Link,
                to: downloadPath,
                target: "_blank",
                rel: "noopener noreferrer",
              }
              : {
                as: "button",
                type: "button",
                disabled: true,
              })}
            variant="outline-primary"
            // Hide at large and above.
            className={`download-schedule-button mb-2 text-decoration-none float-end ${currentView === "realtime" ? "d-none" : "d-block d-lg-none"}`}
            aria-label="Download PDF schedule"
            onClick={downloadPath ? handleDownloadClick : undefined}
          >
            <i className="far fa-file-pdf"></i> Download Schedule
          </Button>
        </Col>
        <Col xs={12}>
          <RiderAlerts routeId={routeId} />
        </Col>
      </Row>
      <Row>
        <Col>
          <Tab.Container
            id="route-tabs"
            activeKey={currentView}
            onSelect={setCurrentView}

          >
            <Row>
              <Col>
                <Nav className="route-schedule-realtime-nav-tabs mb-3 nav-justified w-100 flex-auto justify-content-end">
                  <Nav.Item className="d-flex justify-content-end schedule-tab">
                    <Nav.Link eventKey="schedule">
                      <span className="h2 mb-0">{`Route ${routeShortName} Schedule`}</span>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item className="d-flex justify-content-end realtime-tab">
                    <Nav.Link eventKey="realtime">
                      <span className="h2 mb-0">{`Route ${routeShortName} Real-Time Departures`}</span>
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
            </Row>
            <Tab.Content>
              <Tab.Pane className="schedule" eventKey="schedule">
                <ScheduleContainer />
              </Tab.Pane>
              <Tab.Pane className="realtime" eventKey="realtime">
                <RealtimeContainer />
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Col>
      </Row>
      <Alert
        hidden={!statusMessage}
        className="my-4 alert-light"
      >
        {statusContent}
      </Alert>
    </div>
  );
}
